import { PageController } from '@client/components/_core/layout/PageController';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VEmailInput } from '@client/components/_shared/forms/view-form/VEmailInput';
import { VEntityOwnerSelect } from '@client/components/_shared/forms/view-form/VEntityOwnerSelect';
import { VPhoneNumberInput } from '@client/components/_shared/forms/view-form/VPhoneNumberInput';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { GovernanceChecks } from '@client/components/mspr/GovernanceChecks';
import { TaskList } from '@client/components/tasks/TaskList';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { ICONS } from '@client/styles/theme';
import { abnInputRule, leiInputRule } from '@client/utils/validation';
import { MSPR_PROVIDER_TYPE_LABELS, MSPR_PROVIDER_TYPES } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Row } from 'antd';
import { useCanEditMsprProvider } from '../hooks';
import { serviceProviderFormStore } from '../store';
import { MaterialArrangementsTable } from './MaterialArrangementsTable';
import { ProviderRisks } from './ProviderRisks';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/details/$id/manage')({
    staticData: {
        ui: {
            useParentTitle: true,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ManageMSP />
        </PageController>
    );
}

function ManageMSP() {
    const { trpc } = useTrpc();
    const { id: providerId } = Route.useParams();
    const serviceProvider = serviceProviderFormStore.use.formData();
    const { data: ratings } = trpc.mspr.definition.listRatings.useQuery();
    const { data: statuses } = trpc.mspr.definition.listStatuses.useQuery();
    const canEdit = useCanEditMsprProvider();
    const readOnly = !canEdit;
    const { enabledModules } = useOrg();
    const isRiskFeatureEnabled = enabledModules.includes('risk');

    const VEntityOwnerSelectInstance = VEntityOwnerSelect({
        entity: 'msprProvider',
        formStore: serviceProviderFormStore,
        isReadOnly: readOnly,
    });

    return (
        <>
            {serviceProvider ? (
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                        <Row gutter={[16, 16]} className="h-full">
                            <Col span={24}>
                                <Card
                                    title={
                                        <CardTitle
                                            icon={<ICONS.supplier />}
                                            title="Material service provider details"
                                        />
                                    }
                                    size="small"
                                    className="h-full"
                                >
                                    <Row gutter={[16, 8]}>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VTextInput
                                                label="Business name"
                                                field="businessName"
                                                formStore={serviceProviderFormStore}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>

                                        <Col xs={24} sm={12} lg={6}>
                                            <VDropdown
                                                label="Rating"
                                                field="rating"
                                                formStore={serviceProviderFormStore}
                                                options={(ratings || []).map((rating) => ({
                                                    value: rating,
                                                }))}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VDropdown
                                                label="Status"
                                                field="status"
                                                formStore={serviceProviderFormStore}
                                                options={(statuses || []).map((status) => ({
                                                    value: status,
                                                }))}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VEntityOwnerSelectInstance.HandlingParty />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VEntityOwnerSelectInstance.Owner />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VTextInput
                                                label="Contact person"
                                                field="contactPerson"
                                                formStore={serviceProviderFormStore}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VEmailInput
                                                label="Contact email"
                                                field="contactEmail"
                                                formStore={serviceProviderFormStore}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VPhoneNumberInput
                                                label="Contact phone"
                                                field="contactPhone"
                                                formStore={serviceProviderFormStore}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VTextInput
                                                label="ABN"
                                                field="abn"
                                                formStore={serviceProviderFormStore}
                                                rules={[abnInputRule]}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VTextInput
                                                label="Legal entity identifier"
                                                field="lei"
                                                formStore={serviceProviderFormStore}
                                                rules={[leiInputRule]}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <VDropdown
                                                label="Provider type"
                                                field="providerType"
                                                formStore={serviceProviderFormStore}
                                                formatValueFn={() =>
                                                    MSPR_PROVIDER_TYPE_LABELS[serviceProvider.providerType]
                                                }
                                                options={Object.values(MSPR_PROVIDER_TYPES).map((type) => ({
                                                    value: type,
                                                    label: MSPR_PROVIDER_TYPE_LABELS[type],
                                                }))}
                                                isReadOnly={readOnly}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col span={24}>
                                <MaterialArrangementsTable providerId={providerId} readOnly={readOnly} />
                            </Col>
                            <Col span={24}>
                                <GovernanceChecks providerId={providerId} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Row gutter={[16, 16]}>
                            {isRiskFeatureEnabled && (
                                <Col span={24}>
                                    <ProviderRisks providerId={providerId} readOnly={readOnly} />
                                </Col>
                            )}
                            <Col span={24}>
                                <TaskList
                                    relatedEntity="msprProvider"
                                    relatedEntityId={serviceProvider.id}
                                    size="small"
                                    hideAddTask={readOnly}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            ) : (
                <SkeletonBlock variant="card" />
            )}
            <Outlet />
        </>
    );
}
